import { useMemo, useContext, useRef, useCallback } from 'react';
import { PatientChartContext } from '../../../contexts/patientChart';
import { MODAL_KEY, DENY, VISIT_URL, RESPONSE_POPUP_KEY, CONFIRMATION_POPUP_KEY, PRODUCT } from '../../../constants/patientChart.constant';
import { EmailReason } from '../../../constants/visits.constant';
import PPButton from '../../subComponents/button';
import Input from '../../subComponents/input';
import Select from '../../subComponents/select';
import { POPUP_TYPE } from '../../subComponents/popup';
import Editor, { pickEditor, pickEditorState } from '../../subComponents/editor/jodit/v2';
import { CommonPortalContext } from '../../../contexts/commonPortal';
import { IPopup } from '../../../constants/commonPortal.constant';
import { FEATURE } from '../../../utils/feature';

const hasFeature = FEATURE();

const JoditEditorId = 'jodit-editor-deny-email-message';

const ProductRow = ({
  product = PRODUCT(),
}) => (
  <div className='flex m-b-5'>
    <div className='flex flex-column flex-fill flex-gap-1'>
      <div className='flex flex-fill align-items-center min-height-30 bg-dark-primary-v2 color-white p-l-2 p-r-2'>Product</div>
      <div className='flex flex-fill align-items-center min-height-30 bold underline color-primary-v4 p-l-2 p-r-2'>{product.displayTitle}</div>
    </div>

    <div className='flex flex-column flex-gap-1'>
      <div className='flex align-items-center min-height-30 bg-dark-primary-v2 color-white p-l-2 p-r-2'>Qty</div>
      <div className='flex align-items-center min-height-30 p-l-2 p-r-2'>{product.Qty} Unit</div>
    </div>
  </div>
);

const ProductRowComponent = new Map([
  [true, (props) => <ProductRow {...props} />],
  [false, () => <></>],
]);

export const Deny = ({
  orderId = 0,
  patientId = '',
  emailReasons: reasons = {},
  denyEmailTemplatesV2: templates = {},
  getScheduledVisits = async (requestBody = {}) => {},
  getAsyncOrders = async (requestBody = {}) => {},
  denyOrder = async (requestBody = {}) => {},
  removeOrdersFromLV = (requestBody = {}) => {},
  getNotesTabInfo = async () => {},
  getEmails = async () => {},
  handler = {
    route: () => {},
    loadProductsListing: () => {},
  },
}) => {
  const {
    deny,
    setDeny,
    loading,
    setLoading,
    visitType,
    joditEditorTemplateForKit,
    handleModalOpen,
    handleMultipleModalClose,
    setJoditEditorTemplateForKit,
  } = useContext(PatientChartContext);
  const editorRef = useRef(new Map());

  const { handler: { handleSnackbarClose, handleSnackbarOpen } } = useContext(CommonPortalContext);

  const emailCollection = useMemo(() => {
    const collection = new Map();
    for (let i = 0; i < templates.data.length; i++) {
      const template = templates.data[i];
      collection.set(template.value, template);
    }
    return collection;
  }, [templates.data]);

  const productDenial = useMemo(() => !!deny.product.ProductId, [deny]);

  /**
   * @type {{
   *  pId: number;
   *  title: string;
   *  subject: string;
   * }[];}
   */
  const filteredEmailTemplates = useMemo(() => {
    if (!templates?.data?.length) {
      return [];
    }

    const denyPId = deny?.product?.ProductId || 0;
    if (!denyPId) {
      return templates.data;
    }
    return templates.data.filter(({ pId }) => denyPId === pId);
  }, [deny, templates]);

  const modifiedTemplates = useMemo(() => ({
    ...templates,
    data: filteredEmailTemplates,
  }), [templates, filteredEmailTemplates]);

  const { ProductRow } = useMemo(() => {  
    const ProductRow = ProductRowComponent.get(productDenial);
    return { ProductRow };
  }, [productDenial]);

  const handleTemplateChange = useCallback(({ updatedState }) => {
    setJoditEditorTemplateForKit(updatedState);
  }, []);

  const joditConfig = useMemo(() => ({
    id: JoditEditorId,
    name: JoditEditorId,
    className: JoditEditorId,
    editorRef: editorRef,
    value: '',
    extras: {
      key: JoditEditorId,
      kitPId: 0,
    },
    onChange: handleTemplateChange,
  }), [handleTemplateChange]);

  const stopLoading = (_loading) => {
    const __loading = new Set(_loading);
    __loading.delete(MODAL_KEY.DENY);
    setLoading(__loading);
  }

  const handleChange = ({ target }) => {
    const template = emailCollection.get(target.value) || { label: '' };
    const subject = templates.subjects.data.get(target.value) || { EmailTemplateBody: '', EmailTemplateSubject: '' };
    setDeny({ ...deny, value: target.value, template, subject });

    if (hasFeature.RICH_TEXT_EDITOR) {
      try {
        const editor = pickEditor({
          name: joditConfig.name,
          editorCollection: joditConfig.editorRef.current,
        });
        editor.value = subject?.EmailTemplateBody || '';
      } catch(e) {
  
      }
    }
  };

  const handleSubjectChange = ({ target }) => {
    setDeny({ ...deny, subject: { ...deny.subject, EmailTemplateSubject: target.value } });
  };

  const handleMessageChange = ({ target }) => {
    setDeny({ ...deny, subject: { ...deny.subject, EmailTemplateBody: target.value } });
  };

  const handleReasonChange = ({ target }) => {
    const collection = new Map(reasons.valuesCollection);
    const reason = collection.get(target.value) || EmailReason();
    setDeny({ ...deny, reason: { ...reason, description: deny.reason.description } });
  };

  const handleReasonDescriptionChange = ({ target }) => {
    setDeny({ ...deny, reason: { ...deny.reason, description: target.value } });
  };

  const handleDeny = async ({ key }) => {
    const requestBody = {
      ProductId: deny.product.ProductId,
      OrderId: orderId,
      PatientId: patientId,
      EmailTemplateId: deny.value,
      EmailSubject: deny.subject.EmailTemplateSubject,
      EmailMessage: deny.subject.EmailTemplateBody,
      DenialReasonId: deny.reason.id,
      DenialReason: deny.reason.label,
      DenialReasonText: deny.reason.description,
    };

    if (hasFeature.RICH_TEXT_EDITOR) {
      const editorState = pickEditorState({
        name: JoditEditorId,
        state: joditEditorTemplateForKit,
      });
      requestBody.EmailMessage = editorState?.value || '';
    }

    const _loading = new Set(loading);
    _loading.add(MODAL_KEY.DENY);
    setLoading(_loading);

    try {
      const { status, data } = await denyOrder(requestBody);
      if (status === 200) {
        stopLoading(_loading);
        setDeny(DENY());
        handleMultipleModalClose.call(this, { keys: [key, MODAL_KEY.DENY] });

        if (productDenial) {
          handleSnackbarOpen(IPopup({
            open: true,
            title: 'Successful',
            type: POPUP_TYPE.SUCCESS,
            message: `${deny.product.displayTitle} is removed from order!`,
            onOk: handleSnackbarClose,
          }));
          handler.loadProductsListing({ OrderId: orderId });
          getNotesTabInfo();
        }

        if (!productDenial) {
          removeOrdersFromLV({ orderIds: [orderId] });
          getScheduledVisits();
          getAsyncOrders();
        }

        // route only when video call in not on going
        const shouldGoToVisits = !productDenial && !_loading.has(MODAL_KEY.TWILIO);
        if (shouldGoToVisits) {
          const visitUrl = VISIT_URL.get(visitType);
          handler.route(visitUrl);
        }

        if (!shouldGoToVisits && hasFeature.IS_SALESFORCE_CONFIGURED) {
          getEmails({ OrderId: orderId });
        }
      }

      if (status !== 200) {
        const { Error: error = 'Failed to deny the order. Please try later.' } = data;
        const heading = 'Error';

        handleModalOpen({
          key: RESPONSE_POPUP_KEY.DENY,
          modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {
      // error handling when needed
    } finally {
      stopLoading(_loading);
    }
  };

  const handleCancelDeny = async ({ keys }) => {
    setDeny({ ...deny, product: PRODUCT() });
    handleMultipleModalClose({ keys });
  };

  const message = useMemo(() => {
    if (!hasFeature.RICH_TEXT_EDITOR) {
      if (!deny?.subject?.EmailTemplateBody) {
        return '';
      }

      return deny.subject.EmailTemplateBody.replace(/(<br>)/gmi, '\n');
    }

    const editorState = pickEditorState({
      name: JoditEditorId,
      state: joditEditorTemplateForKit,
    });
    if (!editorState?.text) {
      return '';
    }

    return editorState.text;
  }, [deny, joditEditorTemplateForKit]);

  const EmailInputCollection = useMemo(() => new Map([
    [false, () => (
      <textarea
        name='emailMessage'
        className='flex flex-fill border-light-grey-1 min-height-160 height-full p-4 font-proxima-nova font-14 color-dark-grey border-box resize-none'
        wrap='soft'
        maxLength={65535}
        value={message}
        onChange={handleMessageChange}
      />
    )],
    [true, () => (
      <Editor
        key={joditConfig.id}
        id={joditConfig.id}
        name={joditConfig.name}
        className={joditConfig.className}
        editorRef={joditConfig.editorRef}
        value={joditConfig.value}
        extras={joditConfig.extras}
        onChange={joditConfig.onChange}
      />
    )],
  ]), [
    message,
    joditConfig,
    handleMessageChange,
  ]);
  const EmailInput = useMemo(() => EmailInputCollection.get(hasFeature.RICH_TEXT_EDITOR), [EmailInputCollection]);

  const Template = (
    <div className='flex flex-column flex-fill m-b-5'>
      <ProductRow product={deny.product} />

      <div className='flex align-items-center m-b-5'>
        <label className="input-label min-width-110 m-r-4">Email Template:</label>
        <Select
          id='templateId'
          labelId="templateId"
          value={deny.value}
          onChange={handleChange}
          items={modifiedTemplates.data}
        />
      </div>

      <div className='flex align-items-center m-b-5'>
        <Input
          name='emailSubject'
          type='text'
          label='Subject Line:'
          className='flex flex-fill'
          labelClassName='width-110'
          max={255}
          value={deny.subject.EmailTemplateSubject}
          onChange={handleSubjectChange}
        />
      </div>

      <div className='flex flex-column flex-fill gap-12 align-items-flex-start m-b-5'>
        <label className="input-label min-width-110 m-r-4">Email Message:</label>
        <EmailInput />
      </div>

      <div className='flex align-items-center m-b-5'>
        <div className='flex flex-column min-width-110 m-r-4'>
          <label className="input-label">Denial Reason:</label>
          <span className='font-10'>(Internal Use Only)</span>
        </div>
        <Select
          id='reasonId'
          labelId="reasonId"
          value={deny.reason.value}
          onChange={handleReasonChange}
          items={reasons.data}
        />
      </div>

      <div className='flex align-items-center m-b-5'>
        <div className="min-width-110 m-r-4"></div>
        <textarea
          name='emailReason'
          className='flex flex-fill border-light-grey-1 min-height-100 p-4 font-proxima-nova font-14 color-dark-grey border-box resize-none'
          wrap='soft'
          maxLength={255} // db can support 5000
          value={deny.reason.description}
          onChange={handleReasonDescriptionChange}
          placeholder='Brief Description:'
        />
      </div>
    </div>
  );

  const _disabled = useMemo(() => (!deny.subject.EmailTemplateSubject || !message || deny.reason.value === -1 || !deny.reason.description), [deny.subject, deny.reason, message]);
  const _loading = useMemo(() => loading.has(MODAL_KEY.DENY), [loading]);

  const Actions = (
    <div className='flex justify-content-flex-end'>
      <PPButton label='Deny' variant='contained' loading={_loading} disabled={_disabled} endGutter onClick={handleDeny.bind(this, { key: CONFIRMATION_POPUP_KEY.DENY, modal: {} })} />
      <PPButton label='Cancel' variant='cancel' disabled={_loading} onClick={handleCancelDeny.bind(this, { keys: [MODAL_KEY.DENY] })} />
    </div>
  );

  return (
    <div className='flex flex-column flex-fill m-5 patient-chart-container'>
      {Template}
      {Actions}
    </div>
  );
};

export default Deny;
